import React, { useCallback, useMemo } from 'react';

import { useObservable } from '~/@components/@hooks';
import {
    ISochiTableHeadCellProps,
    SochiNumberFilter,
    SochiSingleOptionFilter,
    SochiSortSection,
    SochiStringFilter,
    SochiTableHeadCell,
} from '~/@sochi-components/SochiTable/SochiTableHeadCell';
import { SochiDateFilter } from '~/@sochi-components/SochiTable/SochiTableHeadCell/SochiDateFilter';
import {
    ProjectWeightReceiptFilterField,
    projectWeightReceiptsFuelTypes,
    projectWeightReceiptsStatuses,
    projectWeightReceiptsStore,
} from '~/@user-store/weightReceipts/projectWeightReceipts.store';
import { SortInput } from '~/graphql';
import i18n from '~/i18n';

type Props = {
    fieldName: ProjectWeightReceiptFilterField;
    withFilter?: boolean;
    withSort?: boolean;
} & Omit<ISochiTableHeadCellProps, 'sortDirection' | 'children'>;

export const DeliveriesTableHeadCell = ({ fieldName, withFilter, withSort, ...restProps }: Props) => {
    const sortValue = useObservable(() => projectWeightReceiptsStore.sort.value);
    const setSortValue = useCallback((v: SortInput) => projectWeightReceiptsStore.sort.setValue(v), []);

    const sortLabels = useMemo(() => {
        switch (fieldName) {
            case ProjectWeightReceiptFilterField.weight:
            case ProjectWeightReceiptFilterField.truckCapacity:
            case ProjectWeightReceiptFilterField.loadPercentage:
            case ProjectWeightReceiptFilterField.distance:
            case ProjectWeightReceiptFilterField.serialNumber:
            case ProjectWeightReceiptFilterField.deliveryNumber:
                return i18n.SortLowHigh;
            case ProjectWeightReceiptFilterField.plannedStartDate:
                return i18n.SortDate;
            default:
                return i18n.SortString;
        }
    }, [fieldName]);

    const sortSection = useMemo(() => {
        if (!withSort) return null;

        return <SochiSortSection fieldName={fieldName} sort={sortValue} setSort={setSortValue} labels={sortLabels} />;
    }, [withSort, fieldName, sortValue, setSortValue, sortLabels]);

    const filterSection = useMemo(() => {
        if (!withFilter) return null;

        switch (fieldName) {
            case ProjectWeightReceiptFilterField.serialNumber:
            case ProjectWeightReceiptFilterField.deliveryNumber:
            case ProjectWeightReceiptFilterField.dumpTypeName:
            case ProjectWeightReceiptFilterField.solidTestResult:
            case ProjectWeightReceiptFilterField.truckRegistrationNumber:
            case ProjectWeightReceiptFilterField.landfillName:
            case ProjectWeightReceiptFilterField.customerInvoice:
            case ProjectWeightReceiptFilterField.comment:
            case ProjectWeightReceiptFilterField.senderComment:
            case ProjectWeightReceiptFilterField.driverComment:
            case ProjectWeightReceiptFilterField.excavatorOperatorName:
                return (
                    <SochiStringFilter
                        valueGetter={() => projectWeightReceiptsStore.filter.values[fieldName]}
                        setValue={v => projectWeightReceiptsStore.filter.setValue(fieldName, v)}
                    />
                );
            case ProjectWeightReceiptFilterField.weight:
            case ProjectWeightReceiptFilterField.truckCapacity:
            case ProjectWeightReceiptFilterField.loadPercentage:
            case ProjectWeightReceiptFilterField.distance:
                return (
                    <SochiNumberFilter
                        valueGetter={() => projectWeightReceiptsStore.filter.values[fieldName]}
                        setValue={v => projectWeightReceiptsStore.filter.setValue(fieldName, v)}
                    />
                );
            case ProjectWeightReceiptFilterField.plannedStartDate:
                return (
                    <SochiDateFilter
                        valueGetter={() => projectWeightReceiptsStore.filter.values[fieldName]}
                        setValue={v => projectWeightReceiptsStore.filter.setValue(fieldName, v)}
                    />
                );
            case ProjectWeightReceiptFilterField.status:
                return (
                    <SochiSingleOptionFilter
                        values={projectWeightReceiptsStatuses}
                        valueGetter={() =>
                            projectWeightReceiptsStatuses.find(
                                item => projectWeightReceiptsStore.filter.values.status === item.id
                            )
                        }
                        setValue={v => projectWeightReceiptsStore.filter.setValue(fieldName, v?.id)}
                    />
                );
            case ProjectWeightReceiptFilterField.fuel:
                return (
                    <SochiSingleOptionFilter
                        values={projectWeightReceiptsFuelTypes}
                        valueGetter={() =>
                            projectWeightReceiptsFuelTypes.find(
                                item => projectWeightReceiptsStore.filter.values.fuel === item.id
                            )
                        }
                        setValue={v => projectWeightReceiptsStore.filter.setValue(fieldName, v?.id)}
                    />
                );
            default:
                return null;
        }
    }, [withFilter, fieldName]);

    return (
        <SochiTableHeadCell {...restProps} sortDirection={sortValue?.field === fieldName ? sortValue.direction : null}>
            {sortSection}
            {filterSection}
        </SochiTableHeadCell>
    );
};
